/**
 * Pre-built report dashboards (M21.15, criterion 24).
 *
 * Four read-only dashboards — Headcount, Turnover, Compensation,
 * Compliance — each over a `/api/reports/*` aggregator, rendered
 * with Recharts and exportable to Excel. Filterable by the
 * workspace-scoped legal entity (the top-bar switcher).
 */

import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ErrorPanel, Loading } from "../../components/Loading";
import { PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import type { ReportSeries } from "../../lib/api";
import { exportToXlsx } from "../../lib/export";
import { useLegalEntityId } from "../../lib/legalEntity";

// Solvait brand palette for decorative chart series — brand blue
// leads, then teal / purple / deep, with a neutral tail.
const _PIE_COLORS = [
  "#2260c9", // brand blue
  "#00d3c2", // brand teal
  "#a846bf", // brand purple
  "#292561", // brand deep
  "#7a77a0", // neutral tail
];

function ExportButton({
  rows,
  filename,
}: {
  rows: ReportSeries[];
  filename: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() =>
        void exportToXlsx(
          rows.map((r) => ({ label: r.label, value: r.value })),
          `${filename}.xlsx`,
        )
      }
      className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-muted"
    >
      <Download size={14} /> {t("policies.rep_export_excel")}
    </button>
  );
}

function Metric({
  label,
  value,
  emptyHint,
}: {
  label: string;
  value: string | number;
  /** M3X.UIPOLISH PR 28 — friendly explanation rendered below the
   * value when the metric is 0 / "0". Lets HR understand "no
   * leavers this quarter — retention is strong" instead of just
   * staring at a bare 0. */
  emptyHint?: string;
}) {
  const isZero =
    value === 0 ||
    value === "0" ||
    (typeof value === "string" && /^0(?:\.0+)?\s*(?:SAR)?$/.test(value.trim()));
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-default p-4">
      <div className="text-xs text-text-secondary">{label}</div>
      <div
        className={`mt-1 text-3xl font-bold tabular-nums ${
          isZero ? "text-text-tertiary" : "text-text-primary"
        }`}
      >
        {value}
      </div>
      {isZero && emptyHint && (
        <div className="mt-2 text-xs italic text-text-tertiary">
          {emptyHint}
        </div>
      )}
    </div>
  );
}

function ChartCard({
  title,
  children,
  /** M3X.UIPOLISH PR 28 — when the series feeding the chart is
   * empty, render the explanation in place of the chart. */
  isEmpty,
  emptyHint,
}: {
  title: string;
  children: React.ReactNode;
  isEmpty?: boolean;
  emptyHint?: string;
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-default p-4">
      <h2 className="mb-3 text-sm font-semibold text-text-primary">
        {title}
      </h2>
      <div className="h-64">
        {isEmpty ? (
          <div className="flex h-full items-center justify-center px-4 text-center text-sm text-text-tertiary">
            {emptyHint ?? title}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function BarSeries({ data }: { data: ReportSeries[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" fontSize={11} />
        <YAxis fontSize={11} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="value" fill="#2260c9" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function LineSeries({ data }: { data: ReportSeries[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" fontSize={11} />
        <YAxis fontSize={11} allowDecimals={false} />
        <Tooltip />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#2260c9"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function PieSeries({ data }: { data: ReportSeries[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          outerRadius={90}
          label
        >
          {data.map((_, i) => (
            <Cell key={i} fill={_PIE_COLORS[i % _PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------

export function HeadcountReportPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();
  const q = useQuery({
    queryKey: ["report-headcount", le ?? "_all_"],
    queryFn: () => api.getHeadcountReport(le),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  if (!q.data) return null;
  const d = q.data;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policies.landing_reports_headcount")}
        subtitle={t("policies.rep_headcount_subtitle")}
        breadcrumbs={[{ label: t("policies.rep_breadcrumb"), href: "/reports" }]}
        actions={
          <ExportButton
            rows={d.by_nationality}
            filename="headcount-by-nationality"
          />
        }
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Metric
          label={t("policies.rep_metric_active_headcount")}
          value={d.active_total}
          emptyHint={t(
            "reports_empty.headcount_no_active",
            "No active employees in this entity yet. Add an employee via People → Employees → Hire.",
          )}
        />
        <Metric
          label={t("policies.rep_metric_total_incl_terminated")}
          value={d.grand_total}
          emptyHint={t(
            "reports_empty.headcount_no_total",
            "No employee records exist for this entity yet.",
          )}
        />
        <Metric
          label={t("policies.rep_metric_nationalities")}
          value={d.by_nationality.length}
          emptyHint={t(
            "reports_empty.headcount_no_nationalities",
            "Nationality count appears once employees are added.",
          )}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title={t("policies.rep_chart_by_nationality")}
          isEmpty={d.by_nationality.length === 0}
          emptyHint={t(
            "reports_empty.headcount_chart_nationality",
            "Nationality breakdown appears once at least one employee is hired.",
          )}
        >
          <PieSeries data={d.by_nationality} />
        </ChartCard>
        <ChartCard
          title={t("policies.rep_chart_by_status")}
          isEmpty={d.by_status.length === 0}
          emptyHint={t(
            "reports_empty.headcount_chart_status",
            "Active / terminated split appears once employees are added.",
          )}
        >
          <BarSeries data={d.by_status} />
        </ChartCard>
        <ChartCard
          title={t("policies.rep_chart_hires_12m")}
          isEmpty={d.hires_by_month.length === 0}
          emptyHint={t(
            "reports_empty.headcount_chart_hires",
            "Hiring trend appears once the first employee is hired through Solvait.",
          )}
        >
          <LineSeries data={d.hires_by_month} />
        </ChartCard>
      </div>
    </div>
  );
}

export function TurnoverReportPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();
  const q = useQuery({
    queryKey: ["report-turnover", le ?? "_all_"],
    queryFn: () => api.getTurnoverReport(le),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  if (!q.data) return null;
  const d = q.data;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policies.landing_reports_turnover")}
        subtitle={t("policies.rep_turnover_subtitle")}
        breadcrumbs={[{ label: t("policies.rep_breadcrumb"), href: "/reports" }]}
        actions={
          <ExportButton
            rows={d.terminations_by_month}
            filename="terminations-by-month"
          />
        }
      />
      <div className="grid grid-cols-2 gap-3">
        <Metric
          label={t("policies.rep_metric_leavers")}
          value={d.leaver_count}
          emptyHint={t(
            "reports_empty.turnover_no_leavers",
            "No terminations recorded for this period. Strong retention — or no separation workflows have been processed yet.",
          )}
        />
        <Metric
          label={t("policies.rep_metric_avg_tenure")}
          value={t("policies.rep_metric_avg_tenure_value", { years: d.avg_tenure_years })}
          emptyHint={t(
            "reports_empty.turnover_no_tenure",
            "Average tenure is computed from terminated employees. Add a separation to populate.",
          )}
        />
      </div>
      <ChartCard
        title={t("policies.rep_chart_terminations_12m")}
        isEmpty={d.terminations_by_month.length === 0}
        emptyHint={t(
          "reports_empty.turnover_chart_terminations",
          "Termination trend appears once the first separation workflow completes.",
        )}
      >
        <BarSeries data={d.terminations_by_month} />
      </ChartCard>
    </div>
  );
}

export function CompensationReportPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();
  const q = useQuery({
    queryKey: ["report-compensation", le ?? "_all_"],
    queryFn: () => api.getCompensationReport(le),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  if (!q.data) return null;
  const d = q.data;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policies.landing_reports_compensation")}
        subtitle={t("policies.rep_compensation_subtitle")}
        breadcrumbs={[{ label: t("policies.rep_breadcrumb"), href: "/reports" }]}
        actions={
          <ExportButton
            rows={d.by_salary_band}
            filename="salary-bands"
          />
        }
      />
      <div className="grid grid-cols-2 gap-3">
        <Metric
          label={t("policies.rep_metric_total_monthly_basic")}
          value={t("policies.rep_metric_total_monthly_basic_value", { value: d.total_monthly_basic.toLocaleString() })}
          emptyHint={t(
            "reports_empty.comp_no_basic",
            "Total appears once active contracts with basic-salary > 0 exist.",
          )}
        />
        <Metric
          label={t("policies.rep_metric_active_contracts")}
          value={d.contract_count}
          emptyHint={t(
            "reports_empty.comp_no_contracts",
            "Active contract count appears once contracts are issued. People → Contracts.",
          )}
        />
      </div>
      <ChartCard
        title={t("policies.rep_chart_by_salary_band")}
        isEmpty={d.by_salary_band.length === 0}
        emptyHint={t(
          "reports_empty.comp_chart_bands",
          "Salary-band distribution appears once active contracts exist.",
        )}
      >
        <BarSeries data={d.by_salary_band} />
      </ChartCard>
    </div>
  );
}

export function ComplianceReportPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();
  const q = useQuery({
    queryKey: ["report-compliance", le ?? "_all_"],
    queryFn: () => api.getComplianceReport(le),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  if (!q.data) return null;
  const d = q.data;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policies.landing_reports_compliance")}
        subtitle={t("policies.rep_compliance_subtitle")}
        breadcrumbs={[{ label: t("policies.rep_breadcrumb"), href: "/reports" }]}
        actions={
          <ExportButton
            rows={d.saudization}
            filename="saudization"
          />
        }
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Metric
          label={t("policies.rep_metric_iqama_90d")}
          value={d.iqama_expiring_90d}
          emptyHint={t(
            "reports_empty.compl_no_iqama",
            "No iqamas expire in the next 90 days. Mutaba'aa monitors and alerts you 60 days ahead.",
          )}
        />
        <Metric
          label={t("policies.rep_metric_contract_90d")}
          value={d.contract_expiring_90d}
          emptyHint={t(
            "reports_empty.compl_no_contract",
            "No contracts expire in the next 90 days.",
          )}
        />
        <Metric
          label={t("policies.rep_metric_probation_30d")}
          value={d.probation_ending_30d}
          emptyHint={t(
            "reports_empty.compl_no_probation",
            "No probation periods end in the next 30 days.",
          )}
        />
      </div>
      <ChartCard
        title={t("policies.rep_chart_saudization_mix")}
        isEmpty={d.saudization.length === 0}
        emptyHint={t(
          "reports_empty.compl_chart_saudization",
          "Saudization mix appears once employees with nationality data are added.",
        )}
      >
        <PieSeries data={d.saudization} />
      </ChartCard>
    </div>
  );
}
